import { useState, useCallback } from 'react';
import animalApi from '../api/animalApi';

/**
 * 自定义钩子，用于处理动物数据的获取和状态管理
 * 
 * @returns {Object} 包含动物数据状态和操作方法的对象
 */
const useAnimal = () => {
  // 当前显示的动物数据
  const [animal, setAnimal] = useState(null);
  // 加载状态
  const [loading, setLoading] = useState(false);
  // 错误状态
  const [error, setError] = useState(null);
  // 控制动物卡片显示
  const [showAnimal, setShowAnimal] = useState(false);

  /**
   * 获取随机动物数据
   */
  const fetchRandomAnimal = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 如果已经显示动物，先隐藏卡片
      if (showAnimal) {
        setShowAnimal(false);
        // 短暂延迟，让卡片消失动画完成
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // 获取新的随机动物数据
      const newAnimal = await animalApi.getRandomAnimal();
      setAnimal(newAnimal);
      
      // 显示新的动物卡片
      setShowAnimal(true);
    } catch (err) {
      // 记录错误但不阻止应用继续运行
      // API服务已经有备选数据处理机制
      setError(err.message || '获取随机动物数据失败，请重试');
      console.error('获取动物数据错误:', err);
      
      // 如果API服务返回了备选数据，依然显示
      if (animal) {
        setShowAnimal(true);
      }
    } finally {
      setLoading(false);
    }
  }, [showAnimal, animal]);

  /**
   * 通过ID获取特定动物数据
   * 
   * @param {string} id 动物ID
   */
  const fetchAnimalById = useCallback(async (id) => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    setShowAnimal(false);
    
    try {
      // 短暂延迟，让卡片消失动画完成
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // 获取指定ID的动物数据
      const specificAnimal = await animalApi.getAnimalById(id);
      setAnimal(specificAnimal);
      
      // 显示动物卡片
      setShowAnimal(true);
    } catch (err) {
      setError(`获取ID为${id}的动物数据失败: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    animal,
    loading,
    error,
    showAnimal,
    fetchRandomAnimal,
    fetchAnimalById,
    // 暴露状态设置方法，便于外部组件直接控制
    setAnimal,
    setLoading,
    setError,
    setShowAnimal
  };
};

export default useAnimal; 